<template>
  <master-layout id="profile-edit" no-bottom-pad>
    <div
      class="bg-grey-5 min-h-full lg:w-[calc(100vw-292px)]"
      :class="{
        'top-0': !showHeader,
        'top-navbar-height': showHeader,
      }"
    >
      <div class="relative">
        <!-- Back chevron: floating (sits over the page top) -->
        <BackButton
          variant="floating"
          :is-gic="isGICConfType"
          data-test-id="gensapp-editprofile-back"
          @click="back"
        />

        <div class="mx-6 lg:w-[680px] lg:mx-auto">
          <!-- Title: reserves the chevron's height + gap above it so the column is unchanged -->
          <div class="flex flex-col items-start py-[16px]">
            <h1
              class="page-title mt-[calc(var(--safe-area-inset-top,0px)+56px)]"
              :class="{ '!mt-[100px]': isIos }"
            >
              {{ $t('copy.profilePage.editProfile') }}
            </h1>
          </div>

          <!-- Form -->
          <div class="flex flex-col gap-[24px] pb-[40px]">
            <FileUploader :is-gic="isGICConfType" />

            <!-- Editable detail fields -->
            <div
              v-for="(value, key, index) in details"
              :key="index"
              :id="key === 'email' ? 'edit-email-section' : null"
              class="flex flex-col gap-[4px] w-full"
            >
              <div
                v-html="value.description"
                class="font-proxima-nova font-semibold text-[16px] leading-[20px] text-grey-100"
              ></div>
              <input
                :ref="key"
                type="text"
                :value="getDetailFormVariable(key)"
                @input="updateDetail(key, $event.target.value)"
                class="w-full h-[40px] pl-[12px] bg-white border rounded-[8px] font-proxima-nova text-[16px] leading-[24px] text-grey-60"
                :class="
                  isNameEmpty(key)
                    ? 'border-[#B80A0A] focus-color-red'
                    : 'border-grey-20 focus-color-blue'
                "
              />
              <div v-if="isNameEmpty(key)" class="italic text-[#B80A0A] text-[12px] leading-[20px]">
                {{ $t('copy.profilePage.emptyName') }}
              </div>
            </div>

            <!-- Show my email toggle (toggle left, label right) -->
            <Toggle
              :value="formEmailFlag"
              :is-gic="isGICConfType"
              @input="updateEmailFlag"
              class="gap-[8px]"
            >
              <template #right>
                <span class="font-proxima-nova text-[16px] leading-[20px] text-grey-100">
                  {{ $t('copy.profilePage.showMyEmail') }}
                </span>
              </template>
            </Toggle>

            <!-- Account login email (read-only) -->
            <div class="flex flex-col gap-[4px] w-full">
              <div class="font-proxima-nova font-semibold text-[16px] leading-[20px] text-grey-100">
                {{ $t('copy.profilePage.accountLoginEmail') }}
              </div>
              <div
                class="w-full h-[40px] pl-[12px] flex items-center bg-grey-5 border border-grey-20 rounded-[8px] font-proxima-nova text-[16px] leading-[24px] text-grey-60"
              >
                {{ email }}
              </div>
            </div>

            <!-- Socials -->
            <div id="edit-socials-section" class="flex flex-col gap-[12px] w-full">
              <div class="font-proxima-nova font-semibold text-[16px] leading-[20px] text-grey-80">
                {{ $t('copy.profilePage.socialMedia.addSocialsTitle') }}
              </div>
              <div v-for="(value, key, index) in socials" :key="index" class="relative">
                <input
                  type="text"
                  :value="getSocialFormVariable(key)"
                  @input="updateSocial(key, $event.target.value)"
                  class="w-full h-[40px] pl-[12px] pr-[52px] bg-white border border-grey-20 rounded-[8px] font-proxima-nova text-[16px] leading-[24px] text-grey-60 focus-color-blue"
                  :placeholder="value.placeholder"
                />
                <div class="absolute inset-y-0 right-[12px] flex items-center pointer-events-none">
                  <img :src="value.icon" class="w-[20px] h-[20px]" />
                </div>
              </div>
            </div>

            <!-- Save -->
            <button
              :class="[
                'font-aktiv-grotesk-trial w-full mx-auto rounded-[100px] text-white text-[18px] leading-[28px] text-center py-[8px] mt-[16px] mb-16 lg:mb-0',
                isGICConfType ? 'bg-orange' : 'bg-lightblue',
              ]"
              @click="saveProfile()"
            >
              {{ $t('copy.profilePage.saveChanges') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Unsaved-changes prompt. Tapping outside (backdrop) dismisses it and is
         treated like "Keep editing" — it cancels the exit without discarding. -->
    <ion-modal
      :is-open="showUnsavedPrompt"
      :backdrop-dismiss="true"
      :keyboard-close="false"
      style="--width: 90%; --max-width: 400px"
      @didDismiss="keepEditing"
    >
      <div class="flex flex-col items-center p-6 text-center">
        <h2 class="font-aktiv-grotesk-trial text-[22px] leading-[28px] text-[#21252C]">
          {{ $t('copy.profilePage.unsavedTitle') }}
        </h2>
        <p class="mt-3 font-proxima-nova text-[16px] leading-[24px] text-grey-60">
          {{ $t('copy.profilePage.unsavedBody') }}
        </p>
        <div class="mt-6 flex w-full flex-col gap-3">
          <button
            :class="[
              'font-aktiv-grotesk-trial w-full rounded-[100px] text-white text-[18px] leading-[28px] text-center py-[8px] focus:outline-none',
              isGICConfType ? 'bg-orange' : 'bg-lightblue',
            ]"
            @click="keepEditing"
          >
            {{ $t('copy.profilePage.unsavedKeepEditing') }}
          </button>
          <button
            class="font-aktiv-grotesk-trial w-full py-[8px] text-[18px] leading-[28px] text-[#B80A0A] focus:outline-none"
            @click="discardChanges"
          >
            {{ $t('copy.profilePage.unsavedDiscard') }}
          </button>
        </div>
      </div>
    </ion-modal>
  </master-layout>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { IonModal } from '@ionic/vue';
import Toggle from '@/components/common/Toggle.vue';
import FileUploader from '@/components/Profile/FileUploader.vue';
import BackButton from '@/components/common/BackButton.vue';
import platformMixin from '@/mixins/platformMixin';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import { getCurrentConferenceType } from '@/utils/conference-type';

export default {
  name: 'Profile',
  mixins: [platformMixin],
  components: {
    Toggle,
    FileUploader,
    IonModal,
    BackButton,
  },
  data() {
    return {
      nameEmpty: false,
      isSamsung: false,
      isGICConfType: false,
      showUnsavedPrompt: false,
      confirmedLeave: false,
      pendingNext: null,
    };
  },
  computed: {
    ...mapState('profile', ['confYear', 'conferences']),
    ...mapState('social', [
      'formName',
      'formOrganization',
      'formTitle',
      'formEmail',
      'formEmailFlag',
      'formInstagram',
      'formFacebook',
      'formX',
      'formLinkedIn',
      'formPicture',
      'uploadURL',
    ]),
    // Saved (server) values, aliased so they don't clash with the account-login
    // `email` getter below. Used to detect unsaved edits.
    ...mapState('social', {
      savedName: 'name',
      savedOrganization: 'organization',
      savedTitle: 'title',
      savedEmail: 'email',
      savedEmailFlag: 'emailFlag',
      savedInstagram: 'instagram',
      savedFacebook: 'facebook',
      savedX: 'x',
      savedLinkedIn: 'linkedin',
    }),
    ...mapGetters('profile', ['email']),

    showHeader() {
      return this.$route.path !== '/home';
    },
    hasUnsavedChanges() {
      return (
        this.formName !== this.savedName ||
        this.formOrganization !== this.savedOrganization ||
        this.formTitle !== this.savedTitle ||
        this.formEmail !== this.savedEmail ||
        this.formEmailFlag !== this.savedEmailFlag ||
        this.formInstagram !== this.savedInstagram ||
        this.formFacebook !== this.savedFacebook ||
        this.formX !== this.savedX ||
        this.formLinkedIn !== this.savedLinkedIn ||
        Boolean(this.formPicture)
      );
    },
    details() {
      return {
        name: {
          description: this.$t('copy.profilePage.displayName'),
          value: 'formName',
        },
        organization: {
          description: this.$t('copy.profilePage.organization'),
          value: 'formOrganization',
        },
        title: {
          description: this.$t('copy.profilePage.title'),
          value: 'formTitle',
        },
        email: {
          description: this.$t('copy.profilePage.email'),
          value: 'formEmail',
        },
      };
    },
    socials() {
      return {
        instagram: {
          value: 'formInstagram',
          placeholder: this.$t('copy.profilePage.socialMedia.addInstagram'),
          icon: require('../../../public/assets/icons/icon-instagram-default.svg'),
        },
        facebook: {
          value: 'formFacebook',
          placeholder: this.$t('copy.profilePage.socialMedia.addFacebook'),
          icon: require('../../../public/assets/icons/icon-facebook-default.svg'),
        },
        x: {
          value: 'formX',
          placeholder: this.$t('copy.profilePage.socialMedia.addX'),
          icon: require('../../../public/assets/icons/icon-x-default.svg'),
        },
        linkedin: {
          value: 'formLinkedIn',
          placeholder: this.$t('copy.profilePage.socialMedia.addLinkedIn'),
          icon: require('../../../public/assets/icons/icon-linkedin-default.svg'),
        },
      };
    },
  },
  async mounted() {
    if (navigator.userAgent.match(/samsung/i)) {
      this.isSamsung = true;
    }
  },
  async ionViewWillEnter() {
    // Ionic caches this page instance, so clear any stale guard state from a
    // previous visit before re-arming the unsaved-changes prompt.
    this.confirmedLeave = false;
    this.pendingNext = null;
    this.showUnsavedPrompt = false;
    // Populate the form when landing here directly (e.g. on a hard refresh),
    // not only when arriving from the profile page. No-op when already loaded.
    await this.$store.dispatch('social/fetchSocialProfile');
  },
  async ionViewDidEnter() {
    this.isGICConfType = getCurrentConferenceType(this.confYear, this.conferences) === 'GIC';
    FirebaseAnalytics.setScreenName({
      screenName: 'profile - edit',
      nameOverride: 'ProfilePage - Edit',
    });
    await this.scrollToSection();
  },
  methods: {
    isNameEmpty(key) {
      return this.nameEmpty && key === 'name';
    },
    back() {
      // Navigation is intercepted by beforeRouteLeave, which prompts when there
      // are unsaved changes and resets the form on discard.
      this.nameEmpty = false;
      this.$router.push('/profile');
    },
    discardChanges() {
      this.showUnsavedPrompt = false;
      this.confirmedLeave = true;
      this.$store.commit('social/resetFormData');
      const next = this.pendingNext;
      this.pendingNext = null;
      if (next) {
        next();
      } else {
        this.$router.push('/profile');
      }
    },
    keepEditing() {
      this.showUnsavedPrompt = false;
      const next = this.pendingNext;
      this.pendingNext = null;
      // next(false) aborts the pending navigation and stays on this page.
      if (next) next(false);
    },
    getDetailFormVariable(detailType) {
      if (detailType === 'name') {
        return this.formName;
      } else if (detailType === 'organization') {
        return this.formOrganization;
      } else if (detailType === 'title') {
        return this.formTitle;
      } else if (detailType === 'email') {
        return this.formEmail;
      }
    },
    updateDetail(detailType, newDetailInput) {
      if (detailType === 'name') {
        this.nameEmpty = false;
        this.$store.commit('social/setFormName', newDetailInput);
      } else if (detailType === 'organization') {
        this.$store.commit('social/setFormOrganization', newDetailInput);
      } else if (detailType === 'title') {
        this.$store.commit('social/setFormTitle', newDetailInput);
      } else if (detailType === 'email') {
        this.$store.commit('social/setFormEmail', newDetailInput);
      }
    },
    getSocialFormVariable(socialType) {
      if (socialType === 'instagram') {
        return this.formInstagram;
      } else if (socialType === 'facebook') {
        return this.formFacebook;
      } else if (socialType === 'x') {
        return this.formX;
      } else if (socialType === 'linkedin') {
        return this.formLinkedIn;
      }
    },
    updateSocial(socialType, newSocialInput) {
      if (socialType === 'instagram') {
        this.$store.commit('social/setFormInstagram', newSocialInput);
      } else if (socialType === 'facebook') {
        this.$store.commit('social/setFormFacebook', newSocialInput);
      } else if (socialType === 'x') {
        this.$store.commit('social/setFormX', newSocialInput);
      } else if (socialType === 'linkedin') {
        this.$store.commit('social/setFormLinkedIn', newSocialInput);
      }
    },
    updateEmailFlag(newValue) {
      this.$store.commit('social/setFormEmailFlag', newValue);
    },
    async saveProfile() {
      if (this.formName === null || this.formName === '') {
        this.nameEmpty = true;
        this.backToTop();
        this.$refs.name[0].focus();
      } else {
        // only execute if there is a new image to upload
        if (this.formPicture) {
          //file upload function
          // 1. get the uploadURL (full presigned URL by AWS)
          await this.$store.dispatch('social/getUploadURL');

          // 2. Upload the blob to S3 via the presigned URL. CapacitorHttp (enabled
          // globally) intercepts axios/fetch but cannot serialize Blob bodies
          // (CapacitorUrlRequestError error 0), so use the original browser fetch
          // Capacitor preserves on window. The bucket allows CORS PUT from any origin.
          const webFetch = window.CapacitorWebFetch || window.fetch;
          const uploadRes = await webFetch(this.uploadURL, {
            method: 'PUT',
            body: this.formPicture,
            headers: { 'Content-Type': 'application/octet-stream' },
          });
          if (!uploadRes.ok) {
            throw new Error(`S3 upload failed: ${uploadRes.status}`);
          }
        }
        await this.$store.dispatch('social/updateSocialProfile');
        await this.$store.dispatch('social/forceFetchSocialProfile');
        this.$router.push('/profile');
      }
    },
    backToTop() {
      const elem = document.getElementById('profile-edit');
      elem.scrollToTop(1000);
    },
    sectionElementId(section) {
      if (section === 'email') return 'edit-email-section';
      if (section === 'socials') return 'edit-socials-section';
      return null;
    },
    async scrollToSection() {
      const id = this.sectionElementId(this.$route.query.section);
      if (!id) return;
      await this.$nextTick();
      const content = document.getElementById('profile-edit');
      const target = document.getElementById(id);
      if (!content || !target || typeof content.getScrollElement !== 'function') return;
      const scrollEl = await content.getScrollElement();
      const y =
        target.getBoundingClientRect().top -
        scrollEl.getBoundingClientRect().top +
        scrollEl.scrollTop;
      content.scrollToPoint(0, Math.max(y - 16, 0), 500);
    },
  },
  beforeRouteLeave(to, from, next) {
    // Adding a photo routes through /profile/crop and back — that's part of
    // editing, not leaving — so let it through without prompting.
    if (to.path === '/profile/crop' || this.confirmedLeave || !this.hasUnsavedChanges) {
      next();
      return;
    }
    this.pendingNext = next;
    this.showUnsavedPrompt = true;
  },
};
</script>
<style scoped>
.focus-color-blue:focus {
  outline: none;
  border: 1px solid rgb(59 130 246);
}
.focus-color-red:focus {
  outline: none;
  border: 1px solid #dd3333;
}
</style>
